import { useState } from 'react';
import { Input } from '../../../shared/components/Input';
import Button from '../../../shared/components/Button';
import { CheckboxMultiSelect } from './CheckboxMultiSelect';
import { formatPrice } from './charterFormat';
import type { CharterAddon, CharterPackage, CreateAddonPayload } from '../types';

interface Props {
  initial?: CharterAddon | null;
  packages: CharterPackage[];
  onSubmit: (values: CreateAddonPayload) => void;
  onCancel: () => void;
  isSaving: boolean;
}

export function AddonForm({ initial, packages, onSubmit, onCancel, isSaving }: Props) {
  const [name, setName] = useState(initial?.name ?? '');
  const [priceMajor, setPriceMajor] = useState(initial && initial.price ? (initial.price / 100).toString() : '');
  const [extraDuration, setExtraDuration] = useState(
    initial?.extraDurationMinutes ? String(initial.extraDurationMinutes) : '',
  );
  const [packageIds, setPackageIds] = useState<string[]>(initial?.packageIds ?? []);
  const [error, setError] = useState('');

  const options = packages.map((p) => ({
    value: p.id,
    label: p.name,
    sublabel: formatPrice(p.price, p.currency),
  }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    const priceNum = priceMajor.trim() === '' ? 0 : parseFloat(priceMajor);
    if (!Number.isFinite(priceNum) || priceNum < 0) {
      setError('Price cannot be negative (0 = free)');
      return;
    }
    const extra = extraDuration.trim() === '' ? undefined : parseInt(extraDuration, 10);
    if (extra !== undefined && (!Number.isFinite(extra) || extra < 0)) {
      setError('Extra duration cannot be negative');
      return;
    }

    onSubmit({
      name: name.trim(),
      price: Math.round(priceNum * 100),
      extraDurationMinutes: extra,
      packageIds: packageIds.length > 0 ? packageIds : undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <Input
        label="Name"
        placeholder="e.g. Rush 48h Turnaround"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Price (₦) — 0 for free"
          type="number"
          min={0}
          step="0.01"
          placeholder="0.00"
          value={priceMajor}
          onChange={(e) => setPriceMajor(e.target.value)}
        />
        <Input
          label="Extra duration (minutes, optional)"
          type="number"
          min={0}
          placeholder="0"
          value={extraDuration}
          onChange={(e) => setExtraDuration(e.target.value)}
        />
      </div>
      <CheckboxMultiSelect
        label="Attachable to packages (optional)"
        options={options}
        selected={packageIds}
        onChange={setPackageIds}
        emptyLabel="No packages yet — leave empty to attach to any."
      />

      {error && <p className="text-sm text-red-500">{error}</p>}

      <div className="flex justify-end gap-3 pt-1">
        <Button type="button" variant="outline" size="sm" className="h-[40px]" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" className="h-[40px]" disabled={isSaving} loading={isSaving}>
          {initial ? 'Save changes' : 'Create add-on'}
        </Button>
      </div>
    </form>
  );
}
